import React, { useMemo, useContext, useState, useRef, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import CustomTable from '../../../common/Table/CustomTable/CustomTable';
import { columns, monthly1columns, monthly2columns, monthly3columns } from './helpers';
import {
  addNewTimesheet,
  changePartOfPerDiem,
  changePerDiem,
  changeStatus,
  checkUncheckAllRows,
  deleteSection,
  updateTimesheet
} from '../../../../actions/timesheet';
// eslint-disable-next-line import/no-cycle
import TimesheetTableWrapper from './TimesheetTableWrapper/TimesheetTableWrapper';
import { useWindowSize } from '../../../../utils/hooks';
import { responsiveSizeTimesheet, timesheetsStatuses } from '../../../../utils/constants';
import TimesheetsCardList from './TimesheetsCardList/TimesheetsCardList';
// eslint-disable-next-line import/no-cycle
import { EditContext } from '../Timesheet';
import NotificationModal from '../../../common/ModalWindows/NotificationModal';
import classes from './MAinTable.module.scss';
import PerDiemCreationComponent from '../../../common/PerDiemCreationComponent/PerDiemCreationComponent';
import { getDatesForWeek, getIsPerDiemByDate } from '../helpers';
import colors from '../../../../scss/colors';
import { clearMonth } from '../../../../utils/helpers';
import { idTokenClaims, submissionEnum } from '../../../../utils/enums';
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, createFilterOptions, InputAdornment, Slider, TextField } from '@mui/material';
import { AttachFileOutlined, Delete, DeleteOutlineOutlined, ExpandMoreOutlined, PersonOutline } from '@mui/icons-material';
import { textFieldStyles } from '../../expense/components/MainTable/helper';
import MainButton from '../../../common/buttons/MainButton';
import { buttonSizes, buttonVariants } from '../../../common/buttons/types';
import { ExpandIcon } from '../../../common/Icons';
import { ScrollableSlider } from './ScrollableSlider/ScrollableSlider';
import KeyValueLabel from '../../../common/labels/KeyValueLabel/KeyValueLabel';
import StatusLabel from '../../../common/labels/StatusLabel/StatusLabel';
import { useMsal } from '@azure/msal-react';
import { setNotification } from '../../../../actions/appNotifications';
import ApproverComment from '../ApproverComment/ApproverComment';
import TimesheetSections from './TimesheetSections/TimesheetSections';

const filter = createFilterOptions();

export const PerDiemInfoComponent = ({ perdiems, datesForWeek, timesheetType, choosedMonth, setScrollablePerDiem }) => {
  const { t } = useTranslation();
  const refPerDiem = useRef(null);

  useEffect(() => {
    setScrollablePerDiem(refPerDiem);
  }, [refPerDiem]);

  return timesheetType === submissionEnum.WEEKLY ? (
    <div className={classes.perdiemsInfoContainer}>
      <p>{t('dashboard.timesheet.perdiem')}</p>
      <div className={classes.valuesContainer}>
        {datesForWeek.map((date) => {
          const value = getIsPerDiemByDate(perdiems, date);
          return value ? (
            <CheckIcon key={date.toString()} sx={{ fontSize: '16px', color: colors.PRIMARY_BLUE }} />
          ) : (
            <div key={date.toString()} />
          );
        })}
      </div>
    </div>
  ) : (
    <div className={`${classes.perdiemsInfoContainer} ${classes.monthly}`}>
      <p>{t('dashboard.timesheet.perdiem')}</p>
      <div className={`${classes.divider} ${classes.left}`} />
      <div ref={refPerDiem} className={classes.valuesContainerMonthly}>
        {perdiems
          .filter((item) => dayjs(item.Date).month() === choosedMonth)
          .map((perDiemItem) => (
            <CheckIcon
              key={perDiemItem.Date}
              sx={{ fontSize: '16px', color: perDiemItem.IsPerDiem ? colors.PRIMARY_BLUE : colors.WHITE }}
            />
          ))}
      </div>
      <div className={`${classes.divider} ${classes.right}`} />
      <p>{perdiems.reduce((count, pd) => (pd.IsPerDiem ? count + 1 : count), 0)}</p>
    </div>
  );
};

export default function MainTable({
  timesheet,
  data,
  perdiems,
  startDateOfPeriod,
  submissionType,
  rateType,
  deleteLine,
  timesheetInfo,
  setScroll,
  setScrollableRef,
  setScrollablePerDiem,
  scrollByScroll,
  scroll,
  created,
  placementStatus,
  placementHandler,
  approvers,
  scrollableProps,
  setOpenPop,
  expansion,
  deleteHandler,
  attachmentHandler,
  copyHandler,
  sectionHandler,
  markSectionDirty,
  saveCommentHandler,
  deleteCommentHandler,
  commentsaving,
  exportHandler
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const size = useWindowSize();
  const ctx = useContext(EditContext);
  const { accounts } = useMsal();
  const claims = accounts[0]?.idTokenClaims;
  const isTNEApprover = claims?.[idTokenClaims.IS_TNE_APPROVER] || false;

  const ref = useRef(null);
  const scrollByScrollRef = useRef(null);
  const scrollByScrollPerDiemRef = useRef(null);
  // Local refs/state for non-split scroll handling
  const localScrollablePerDiemRef = useRef(null);
  const [localScroll, setLocalScroll] = useState(0);

  const [expanded, setExpanded] = useState('panel1');
  const [approver, setApprover] = useState(null);
  const [openPerDiemError, setOpenPerDiemError] = useState('');

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const [openRateError, setOpenRateError] = useState('');

  const allowedWorkTypes = useSelector((state) =>
    state.constFromApi.worktypes?.map((item) => ({
      Id: item.value,
      Name: item.label
    }))
  );

  const timesheetType = !timesheetInfo.isApprover ? submissionType : (timesheetInfo.type.includes('Semi') || timesheetInfo.type.includes('Bi'))
    ? timesheetInfo.type.replace(/-?(Daily|Hourly)/, '').toLowerCase()
    : timesheetInfo.type.split('-')[0].toLowerCase();

  const isAllItemsChecked = useMemo(() => data.every((item) => item.checked === true), [data]);

  const currentMonth = useMemo(() => {
    const details = timesheet?.IsSplitApprovalFlow
      ? timesheet?.TimesheetSections?.[0]?.TimesheetDetails?.[0]
      : data?.[0];
    const dayItem = details?.TimesheetHoursByDates?.find((item) => {
      const date = new Date(item?.Day).getDate();
      return date === 1 || date === 16;
    });
    return dayItem ? new Date(dayItem.Day).getMonth() : 0;
  }, [timesheet, data]);

  const datesForWeek = getDatesForWeek(startDateOfPeriod);

  const triggerStatusUpdate = (currentSection) => {
    dispatch(changeStatus(timesheetsStatuses(t).draft.apiStatus, currentSection));
  }

  const changeHandler = (currentSection) => (type, id, newValue, additional) => {
    if (currentSection) {
      markSectionDirty(currentSection);
    }

    const isSplit = timesheet?.IsSplitApprovalFlow;
    let section = isSplit ? timesheet?.TimesheetSections?.find((s) => s.Id === currentSection) : null;

    if (type === 'Approver') {
      if (!isSplit || !section) return;

      const hasDuplicate = timesheet?.TimesheetSections?.some(
        (s) => s.Id !== currentSection && s.ApproverId === newValue?.value
      );

      if (hasDuplicate) {
        dispatch(setNotification({
          type: 'error',
          message: 'This approver is already selected in another section.',
          duration: 3000
        }));

        const updatedSection = {
          ...section,
          Approver: '',
          ApproverId: null
        }

        dispatch(updateTimesheet(updatedSection, currentSection, isSplit));
        return;
      }

      const updatedSection = {
        ...section,
        Approver: newValue,
        ApproverId: newValue?.value || null
      }

      dispatch(updateTimesheet(updatedSection, currentSection, isSplit));
      triggerStatusUpdate(currentSection);
      ctx.setHasChanges(true);
      return;
    }

    let sourceData = isSplit ? (section?.TimesheetDetails || []) : data;
    let timesheetData = sourceData.find((sheet) => sheet.Id === id);

    if (!timesheetData) return;

    let updatedEntry = { ...timesheetData };

    if (type === 'TimesheetHoursByDates') {
      updatedEntry.TimesheetHoursByDates = updatedEntry.TimesheetHoursByDates.some((item) => dayjs(item.Day).isSame(dayjs(additional)))
        ? updatedEntry.TimesheetHoursByDates.map((item) =>
          dayjs(item.Day).isSame(dayjs(additional)) ? { ...item, TotalHrs: newValue || 0 } : item
        )
        : [...updatedEntry.TimesheetHoursByDates, { Day: additional, TotalHrs: newValue || 0 }]
    } else {
      const fieldMap = {
        'Location': 'Location',
        'WorkType': 'WorkType',
        'Code': 'CostCode',
        'Comment': 'Comment',
        'checked': 'checked'
      };

      const fieldName = fieldMap[type];

      if (fieldName) {
        updatedEntry[fieldName] = newValue;
      }
    }

    dispatch(updateTimesheet(updatedEntry, isSplit ? currentSection : null, isSplit));
    ctx.setHasChanges(true);
  };

  const mobileChangeHandler = (newData, currentSection) => {
    if (currentSection) {
      markSectionDirty(currentSection);
    }

    const isSplit = timesheet?.IsSplitApprovalFlow;
    dispatch(updateTimesheet(newData, isSplit ? currentSection : null, isSplit));
    ctx.setHasChanges(true);
  };

  const mainCheckboxHandler = (value) => {
    const timesheets = data.map((item) => ({ ...item, checked: value }));
    dispatch(checkUncheckAllRows(timesheets));
  };

  const addHandler = (currentSection) => () => {
    if (currentSection) {
      markSectionDirty(currentSection);
    }
    const isSplit = timesheet?.IsSplitApprovalFlow;
    dispatch(addNewTimesheet(isSplit ? currentSection : null, isSplit));
    ctx.setHasChanges(true);
  };

  const changePerDiemHandler = (date, value, sectionId = null) => {
    dispatch(
      changePerDiem({
        IsSplitApprovalFlow: timesheet?.IsSplitApprovalFlow,
        SectionId: sectionId,
        Data: {
          Date: dayjs(date).format('YYYY-MM-DD'),
          IsPerDiem: value
        }
      })
    );
    ctx.setHasChanges(true);
  };

  const massiveChangePerDiem = (newPart) => {
    console.log('newPart in massiveChangePerDiem', newPart);
    dispatch(changePartOfPerDiem(newPart));
    ctx.setHasChanges(true);
  };

  useEffect(() => {
    setScrollableRef(ref);
    setScroll(ref);
  }, [ref]);

  useEffect(() => {
    if (!timesheet?.IsSplitApprovalFlow) return;

    scrollByScrollRef?.current?.addEventListener('wheel', scrollByScroll, { passive: false });
    return () => {
      scrollByScrollRef?.current?.removeEventListener('wheel', scrollByScroll, { passive: false });
    };
  }, [
    timesheet?.IsSplitApprovalFlow,
    scrollByScrollRef,
    scrollByScrollRef?.current,
    scrollByScrollPerDiemRef,
    scrollByScrollPerDiemRef?.current,
    scroll
  ]);

  const localScrollLeftRight = useCallback((direction) => {
    if (!ref.current) return;
    const delta = direction === 'left' ? -80 : 80;
    ref.current.scrollLeft += delta;
    if (localScrollablePerDiemRef.current) {
      localScrollablePerDiemRef.current.scrollLeft = ref.current.scrollLeft;
    }
  }, []);

  // Sync slider ← scroll position
  useEffect(() => {
    if (timesheet?.IsSplitApprovalFlow) return;

    const el = ref.current;
    if (!el) return;

    const handleScroll = () => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll === 0) return;

      const percentage = Math.round((el.scrollLeft / maxScroll) * 100);
      setLocalScroll(percentage);

      if (localScrollablePerDiemRef.current) {
        localScrollablePerDiemRef.current.scrollLeft = el.scrollLeft;
      }
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [timesheet?.IsSplitApprovalFlow]);

  // Keyboard arrows
  useEffect(() => {
    if (timesheet?.IsSplitApprovalFlow) return;

    const scrollByKey = (e) => {
      if (!ref.current) return;
      if (e.key === 'ArrowRight') ref.current.scrollLeft += 80;
      if (e.key === 'ArrowLeft') ref.current.scrollLeft -= 80;
    };

    window.addEventListener('keydown', scrollByKey);
    return () => window.removeEventListener('keydown', scrollByKey);
  }, [timesheet?.IsSplitApprovalFlow]);

  useEffect(() => {
    if (timesheet?.IsSplitApprovalFlow) return;

    const wrapper = scrollByScrollRef.current;
    const scrollTarget = ref.current;
    if (!wrapper || !scrollTarget) return;

    let velocity = 0;
    let rafId = null;

    const glide = () => {
      if (Math.abs(velocity) < 0.5) {
        velocity = 0;
        return;
      }
      scrollTarget.scrollLeft += velocity;
      if (localScrollablePerDiemRef.current) {
        localScrollablePerDiemRef.current.scrollLeft = scrollTarget.scrollLeft;
      }
      velocity *= 0.85;
      rafId = requestAnimationFrame(glide);
    };

    const onWheel = (e) => {
      const maxScroll = scrollTarget.scrollWidth - scrollTarget.clientWidth;
      if (maxScroll <= 0) return;

      e.preventDefault();

      const raw = (e.deltaY !== 0 ? e.deltaY : e.deltaX) * 0.4;
      velocity += raw;

      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(glide);
    };

    wrapper.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      wrapper.removeEventListener('wheel', onWheel);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [timesheet?.IsSplitApprovalFlow]);

  // ──────────────────────────────────────────────────────────────────────

  useEffect(() => {
    setExpanded(expansion);
  }, [expansion]);

  useEffect(() => {
    setExpanded(`panel${timesheet?.TimesheetSections?.length > 0 ? timesheet?.TimesheetSections?.length - 1 : 0}`);
  }, []);

  const { scrollableRef, scrollablePerDiem, scrollLeftRight, } = scrollableProps;

  // For non-split flow, no per-section perdiems
  const allSectionsPerDiems = [];

  return (
    <div ref={scrollByScrollRef} className="mt-4 md:mt-8 w-full mb-4">

      {size.width > responsiveSizeTimesheet ? (
        <div className='flex flex-col gap-4'>
          {timesheet?.IsSplitApprovalFlow ? (
            timesheet?.TimesheetSections.map((section, sectionIndex) => (
              <TimesheetSections
                key={section.Id}
                data={data}
                section={section}
                sectionIndex={sectionIndex}
                timesheet={timesheet}
                timesheetType={timesheetType}
                ctx={ctx}
                timesheetInfo={timesheetInfo}
                submissionType={submissionType}
                size={size}
                responsiveSizeTimesheet={responsiveSizeTimesheet}
                handleChange={handleChange}
                isTNEApprover={isTNEApprover}
                approvers={approvers}
                approver={approver}
                setOpenPop={setOpenPop}
                changeHandler={changeHandler}
                deleteHandler={deleteHandler}
                attachmentHandler={attachmentHandler}
                startDateOfPeriod={startDateOfPeriod}
                isAllItemsChecked={isAllItemsChecked}
                mainCheckboxHandler={mainCheckboxHandler}
                addHandler={addHandler}
                allowedWorkTypes={allowedWorkTypes}
                deleteLine={deleteLine}
                rateType={rateType}
                currentMonth={currentMonth}
                setOpenRateError={setOpenRateError}
                saveCommentHandler={saveCommentHandler}
                deleteCommentHandler={deleteCommentHandler}
                commentsaving={commentsaving}
                perdiems={perdiems}
                changePerDiemHandler={changePerDiemHandler}
                expanded={expanded}
              />
            ))
          ) : (
            <div className={`${classes.desktopTableContainer} flex flex-col gap-4 p-6 bg-white rounded-lg shadow-md border-l-5 border-card-default-border`}>

              {timesheetType === submissionEnum.WEEKLY ? (
                <CustomTable
                  data={data}
                  columns={() =>
                    columns(
                      null,
                      startDateOfPeriod,
                      changeHandler(null),
                      t,
                      isAllItemsChecked,
                      mainCheckboxHandler,
                      addHandler(null),
                      allowedWorkTypes,
                      ctx.isEdit,
                      deleteLine,
                      rateType,
                      currentMonth,
                      timesheetType,
                      setOpenRateError,
                      timesheetInfo.status,
                      timesheetInfo.user
                    )
                  }
                  withFooter
                  withLines={{
                    header: false,
                    body: false,
                    footer: false
                  }}
                  footerMargin="0"
                  bodyWithBorderRadius={false}
                />
              ) : (
                <div className={classes.auxContainer}>
                  <CustomTable
                    withExtraFooter
                    data={data}
                    columns={() =>
                      monthly1columns(
                        null,
                        startDateOfPeriod,
                        changeHandler(null),
                        t,
                        isAllItemsChecked,
                        mainCheckboxHandler,
                        addHandler(null),
                        allowedWorkTypes,
                        ctx.isEdit,
                        deleteLine,
                        rateType,
                        currentMonth,
                        timesheetType,
                        setOpenRateError,
                        timesheetInfo.status,
                        timesheetInfo.user
                      )
                    }
                    withFooter
                    withLines={{
                      header: false,
                      body: false,
                      footer: false
                    }}
                    headerStyles={{
                      borderRadius: '10px 0 0 0',
                      height: '68px',
                      borderRight: `2px solid ${colors.DARKEST_NAVY_BLUE}`,
                      borderLeft: `2px solid ${colors.DARKEST_NAVY_BLUE}`
                    }}
                    bodyStyles={{
                      padding: '20px 26px 0 26px',
                      backgroundColor: colors.WHITE,
                      borderRight: `2px solid ${colors.PAGE_BACKGROUND}`,
                      borderLeft: `2px solid ${colors.PAGE_BACKGROUND}`
                    }}
                    footerStyles={{
                      borderRight: `2px solid ${colors.PAGE_BACKGROUND}`,
                      borderLeft: `2px solid ${colors.PAGE_BACKGROUND}`,
                      borderBottom: `2px solid ${colors.PAGE_BACKGROUND}`,
                    }}
                    extraFooterStyles={{
                      borderRight: `2px solid ${colors.PAGE_BACKGROUND}`,
                      borderLeft: `2px solid ${colors.PAGE_BACKGROUND}`,
                      borderBottom: `2px solid ${colors.PAGE_BACKGROUND}`,
                    }}
                    wrapperStyles={{
                      maxWidth: 'max-content',
                      backgroundColor: colors.WHITE,
                      borderRadius: '0'
                    }}
                    footerMargin="0"
                    bodyWithBorderRadius
                  />
                  <div
                    tabIndex={0}
                    ref={ref}
                    className={
                      timesheetType === submissionEnum.MONTHLY || timesheetType === submissionEnum.WEEKLY
                        ? classes.scrollableContainer
                        : classes.scrollableSemiMonthlyContainer
                    }
                  >
                    <CustomTable
                      withActionController={true}
                      withExtraFooter
                      data={timesheetType === submissionEnum.BI_WEEKLY ? data : clearMonth(data, currentMonth)}
                      columns={() =>
                        monthly2columns(
                          null,
                          startDateOfPeriod,
                          changeHandler(null),
                          t,
                          isAllItemsChecked,
                          mainCheckboxHandler,
                          addHandler(null),
                          allowedWorkTypes,
                          ctx.isEdit,
                          deleteLine,
                          rateType,
                          currentMonth,
                          timesheetType,
                          setOpenRateError,
                          timesheetType === submissionEnum.BI_WEEKLY ? data : clearMonth(data, currentMonth),
                          perdiems || [],
                          changePerDiemHandler,
                          !ctx.isEdit ||
                          [timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(
                            timesheetInfo.status
                          ),
                          allSectionsPerDiems,
                          setOpenPerDiemError
                        )
                      }
                      withFooter
                      withLines={{
                        header: false,
                        body: false,
                        footer: false
                      }}
                      headerStyles={{
                        height: '68px',
                        borderRadius: '0',
                        padding: '11px 8px 0 8px'
                      }}
                      wrapperStyles={{
                        backgroundColor: colors.WHITE,
                        borderRadius: '0',
                        boxShadow: '2px 0px 5px 0px rgba(79, 79, 79, 0.10)'
                      }}
                      bodyStyles={{
                        padding: '20px 8px 0 8px',
                        backgroundColor: colors.WHITE
                      }}
                      footerStyles={{
                        padding: '0 8px 20px 8px',
                        backgroundColor: colors.WHITE,
                        borderBottom: `2px solid ${colors.PAGE_BACKGROUND}`,
                      }}
                      extraFooterStyles={{
                        padding: '9px 0',
                        backgroundColor: colors.WHITE,
                        borderBottom: `2px solid ${colors.PAGE_BACKGROUND}`,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                      footerMargin="0"
                      bodyWithBorderRadius={false}
                    />
                  </div>
                  <CustomTable
                    withExtraFooter
                    data={data}
                    columns={() =>
                      monthly3columns(
                        null,
                        startDateOfPeriod,
                        changeHandler(null),
                        t,
                        isAllItemsChecked,
                        mainCheckboxHandler,
                        addHandler(null),
                        allowedWorkTypes,
                        ctx.isEdit,
                        deleteLine,
                        rateType,
                        currentMonth,
                        timesheetType,
                        perdiems || [],
                      )
                    }
                    withFooter
                    withLines={{
                      header: false,
                      body: false,
                      footer: false
                    }}
                    footerMargin="0"
                    headerStyles={{
                      borderRadius: '0 10px 0 0',
                      height: '68px',
                      borderLeft: `2px solid ${colors.DARKEST_NAVY_BLUE}`
                    }}
                    bodyStyles={{
                      padding: '20px 26px 0 26px',
                      backgroundColor: colors.WHITE,
                      borderLeft: `2px solid ${colors.PAGE_BACKGROUND}`,
                      borderRight: `2px solid ${colors.PAGE_BACKGROUND}`,
                    }}
                    footerStyles={{
                      padding: '0 26px 20px 26px',
                      backgroundColor: colors.WHITE,
                      borderLeft: `2px solid ${colors.PAGE_BACKGROUND}`,
                      borderRight: `2px solid ${colors.PAGE_BACKGROUND}`,
                      borderBottom: `2px solid ${colors.PAGE_BACKGROUND}`,
                    }}
                    extraFooterStyles={{
                      padding: '9px 0',
                      backgroundColor: colors.WHITE,
                      borderBottom: `2px solid ${colors.PAGE_BACKGROUND}`,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderLeft: `2px solid ${colors.PAGE_BACKGROUND}`,
                      borderRight: `2px solid ${colors.PAGE_BACKGROUND}`,
                    }}
                    wrapperStyles={{
                      maxWidth: 'max-content',
                      backgroundColor: colors.WHITE,
                      borderRadius: '0 0 10px 0',
                      boxShadow: 'none'
                    }}
                    bodyWithBorderRadius
                  />
                </div>
              )}

                <div className="flex w-full gap-8">
                  {(submissionType === submissionEnum.MONTHLY || submissionType === submissionEnum.BI_MONTHLY || submissionType === submissionEnum.BI_WEEKLY) &&
                    size.width > responsiveSizeTimesheet && (
                      <ScrollableSlider
                        scroll={localScroll}
                        setScroll={setLocalScroll}
                        scrollLeftRight={localScrollLeftRight}
                        submissionType={submissionType}
                        submissionEnum={submissionEnum}
                        scrollableRef={ref}
                        scrollablePerDiem={localScrollablePerDiemRef}
                      />
                    )}
                    {console.log('timesheet?.IsSplitApprovalFlow', timesheet)}

                  <div className='flex gap-5 items-center'
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                  >
                    <MainButton
                      outlined
                      variant={buttonVariants.PRIMARY}
                      size={buttonSizes.XSMALL}
                      rounded
                      action={() => attachmentHandler(null)}
                    >
                      <AttachFileOutlined sx={{ fontSize: '16px' }} />
                      <span>{t('common.general.attachments')} ({timesheet?.TimesheetAttachments?.length || 0})</span>
                    </MainButton>
                  </div>
                </div>
                <div className="flex w-full gap-8">
                  {([
                    timesheetsStatuses(t).rejected.apiStatus,
                    timesheetsStatuses(t).draft.apiStatus,
                    timesheetsStatuses(t).approved.apiStatus,
                    timesheetsStatuses(t).processed.apiStatus,
                    timesheetsStatuses(t).submitted.apiStatus
                  ].includes(timesheet?.Status?.Status) ||
                    (timesheetInfo.isApprover && timesheet?.Status?.Status === timesheetsStatuses(t).submitted.apiStatus)) && (
                      <ApproverComment
                        key={new Date()}
                        comments={(timesheet?.IsSplitApprovalFlow ? timesheet?.TimesheetRejectionReasons : timesheet?.TimesheetRejectionReasons)?.sort((a, b) => (a.Id > b.Id ? -1 : 1))}
                        disabled={
                          !timesheetInfo.isApprover ||
                          [
                            timesheetsStatuses(t).approved.apiStatus,
                            timesheetsStatuses(t).processed.apiStatus,
                            timesheetsStatuses(t).rejected.apiStatus
                          ].includes(timesheet?.Status?.Status)
                        }
                        isApprover={timesheetInfo.isApprover}
                        extraClass="py-6 w-full"
                        saveComment={saveCommentHandler}
                        deleteComment={deleteCommentHandler}
                        commentSaving={commentsaving}
                      />
                    )}
                  </div>
            </div>
          )}
        </div>
      ) : (
        <TimesheetsCardList
          timesheet={timesheet}
          data={data}
          perdiems={perdiems}
          massiveChangePerDiem={massiveChangePerDiem}
          allowedWorkTypes={allowedWorkTypes}
          startDateOfPeriod={startDateOfPeriod}
          addHandler={addHandler}
          deleteLineHandler={deleteLine}
          mobileChangeHandler={mobileChangeHandler}
          rateType={rateType}
          submissionType={timesheetType}
          currentMonth={currentMonth}
          editable={
            ![timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(
              timesheetInfo.status
            ) && !timesheetInfo.isApprover
          }
          status={timesheetInfo.status}
          author={timesheetInfo.user}
          placementStatus={placementStatus}
          placementHandler={placementHandler}
          approvers={approvers}
          created={created}
          deleteHandler={deleteHandler}
          copyHandler={copyHandler}
          setOpenPop={setOpenPop}
          sectionHandler={sectionHandler}
          exportHandler={exportHandler}
        />
      )}
      {openRateError && (
        <NotificationModal open={openRateError} onClose={() => setOpenRateError('')}>
          <p className={classes.errorMessage}>{openRateError}</p>
        </NotificationModal>
      )}
      {openPerDiemError && (
        <NotificationModal open={openPerDiemError} onClose={() => setOpenPerDiemError('')}>
          <p className={classes.errorMessage}>{openPerDiemError}</p>
        </NotificationModal>
      )}
    </div>
  );
}